/**
 * The tower inspector (TRD-09 §9).
 *
 * R-09-21 calls the attribution line — `Damage 24 (+8 Beacon, ×1.2 Frost)` — the
 * second-most-important piece of UI in the game: it teaches synergy in a way no tooltip
 * can. The numbers come from the sim on the snapshot (effective stats, exact refund) and
 * the attribution from `World.statBreakdownFor`, which the synergy engine records at the
 * same accumulator write that changes the mod — the display cannot drift from the rules.
 */
import { CLASS_COLORS, getClass, getTower } from '@prism/content';
import { isTargetingMode, SUPPORT_SURGE_COOLDOWN_WAVES } from '@prism/sim';
import type { ClassId, Command, TargetingMode, UiSnapshot } from '@prism/sim';
import { ClassChip } from './ShopCard.js';
import { formatInterval, formatRange } from './shopCardModel.js';

type PlacedTower = UiSnapshot['placedTowers'][number];

export interface StatSource {
  stat: string;
  op: 'add' | 'mul';
  value: number;
  source: string;
}

interface TowerInspectorProps {
  /** Which number key fires this tower's surge, or null when it has none. */
  surgeKey: number | null;
  tower: PlacedTower;
  /**
   * The buff tile this tower is standing on, if any (playtest request: "when you click to
   * get info tell them they are on a tile"). Resolved by the caller so this component
   * never imports the tile data.
   */
  standingOn: { name: string; description: string; color: string } | null;
  breakdown: readonly StatSource[];
  phase: UiSnapshot['phase'];
  placementsLeft: number;
  moveMode: boolean;
  onMoveMode: (on: boolean) => void;
  onDeselect: () => void;
  dispatch: (cmd: Command) => void;
}

const TARGETING_MODES: TargetingMode[] = ['first', 'last', 'closest', 'strongest', 'weakest'];

/** `+8` / `×1.2` / `+0.4t` — one attribution fragment. */
function fragment(entry: StatSource): string {
  if (entry.op === 'mul') return `×${Math.round(entry.value * 100) / 100}`;
  const v = Math.round(entry.value * 100) / 100;
  return `${v >= 0 ? '+' : ''}${v}`;
}

/** Class sources get their class colour; meta/mutator labels read in shard gold. */
function sourceColor(source: string): string {
  return source in CLASS_COLORS ? CLASS_COLORS[source as ClassId].color : '#e8c95a';
}

function sourceName(source: string): string {
  return source in CLASS_COLORS ? getClass(source as ClassId).name : source;
}

function BreakdownLine({ entries }: { entries: readonly StatSource[] }) {
  if (entries.length === 0) return null;
  return (
    <span className="inspector__breakdown">
      (
      {entries.map((entry, i) => (
        <span key={i} style={{ color: sourceColor(entry.source) }}>
          {i > 0 && ', '}
          {fragment(entry)} {sourceName(entry.source)}
        </span>
      ))}
      )
    </span>
  );
}

export function TowerInspector({
  tower,
  standingOn,
  breakdown,
  phase,
  surgeKey,
  placementsLeft,
  moveMode,
  onMoveMode,
  onDeselect,
  dispatch,
}: TowerInspectorProps) {
  const def = getTower(tower.defId);
  // Every surge state is decided by the sim and read here — see the snapshot's comment.
  const surging = tower.surgeActive || tower.surgeArmed;
  const byStat = (stat: string) => breakdown.filter((b) => b.stat === stat);
  const ground = standingOn;

  const damageEntries = [...byStat('flatDamage'), ...byStat('damageMultiplier')];
  const speedEntries = byStat('attackSpeedMultiplier');
  const rangeEntries = [...byStat('rangeBonus'), ...byStat('rangeMultiplier')];
  const critEntries = [...byStat('critChance'), ...byStat('critMultiplier')];
  const shown = new Set([
    'flatDamage',
    'damageMultiplier',
    'attackSpeedMultiplier',
    'rangeBonus',
    'rangeMultiplier',
    'critChance',
    'critMultiplier',
  ]);
  // Everything else — chain hops, pierce, armor pierce, %-max-HP — in one catch-all
  // row, still attributed. A synergy that grants a stat with no row would be invisible,
  // which is the exact failure R-09-21 exists to prevent.
  const otherEntries = breakdown.filter((b) => !shown.has(b.stat));

  return (
    <div className="group inspector">
      <span className="label">Tower</span>
      <div className="inspector__body">
        <div className="inspector__head">
          <b>
            {def.name}
            {tower.star > 1 ? ` ★${tower.star}` : ''}
          </b>
          {def.classes.map((classId) => (
            <ClassChip key={classId} classId={classId as ClassId} />
          ))}
        </div>

        {ground && (
          // The ground is not a property of the tower, so it gets its own line rather
          // than hiding inside the stat rows — a player who moves this tower loses it.
          <p className="inspector__ground" style={{ borderColor: ground.color }}>
            <b style={{ color: ground.color }}>On {ground.name}</b>
            <span className="muted"> — {ground.description}</span>
          </p>
        )}

        {def.support ? (
          // B-15 — a support tower's whole sheet is its aura and its reach. Combat rows
          // ("Damage 0", "Every 0.02s") would read as broken, not as different.
          <div className="inspector__stats">
            <span className="muted">{def.description}</span>
            <span>
              Aura reach <b>{formatRange(tower.range)}</b> <BreakdownLine entries={rangeEntries} />
            </span>
            {/* Stars scale the aura like they scale a fighter's damage (SIM_VERSION 11). */}
            {tower.star > 1 && (
              <span>
                Aura strength <b>×{def.starScaling[tower.star - 1] ?? 1}</b>
                <span className="muted"> from ★{tower.star}</span>
              </span>
            )}
            {/* B-12 stretch — the carrier's own scoreboard: damage dealt by towers it buffed. */}
            <span>
              Assisted <b>{tower.assistDealt.toLocaleString()}</b>
              <span className="muted"> this run</span>
            </span>
          </div>
        ) : (
        <div className="inspector__stats">
          <span>
            Damage <b>{tower.damage}</b>
            {tower.damage !== tower.baseDamage && (
              <span className="muted"> ({tower.baseDamage} base)</span>
            )}{' '}
            <BreakdownLine entries={damageEntries} />
          </span>
          <span>
            Every <b>{formatInterval(tower.attackInterval)}</b>{' '}
            <BreakdownLine entries={speedEntries} />
          </span>
          <span>
            Range <b>{formatRange(tower.range)}</b> <BreakdownLine entries={rangeEntries} />
          </span>
          {/* Backlog B-12 — the pipeline attributes every hit (R-09-26); show it live. */}
          <span>
            Dealt <b>{tower.damageDealt.toLocaleString()}</b>
            <span className="muted"> this run</span>
          </span>
          {/* B-12 stretch — non-support aura carriers (Beacon towers) earn assists too. */}
          {tower.assistDealt > 0 && (
            <span>
              Assisted <b>{tower.assistDealt.toLocaleString()}</b>
              <span className="muted"> via auras</span>
            </span>
          )}
          {(tower.critChance > 0 || critEntries.length > 0) && (
            <span>
              Crit <b>{Math.round(tower.critChance * 100)}%</b>{' '}
              <BreakdownLine entries={critEntries} />
            </span>
          )}
          {otherEntries.length > 0 && (
            <span>
              Extras{' '}
              {otherEntries.map((entry, i) => (
                <span key={i} style={{ color: sourceColor(entry.source) }}>
                  {i > 0 && ', '}
                  {fragment(entry)} {entry.stat} ({sourceName(entry.source)})
                </span>
              ))}
            </span>
          )}
        </div>
        )}

        <div className="inspector__actions">
          {/* R-09-20's dropdown; the command is replay-logged like any click (R-05-08).
              A support tower has no target to pick, so the dropdown would be a lie. */}
          {!def.support && (
          <select
            value={tower.targetingMode}
            onChange={(e) => {
              const mode = e.target.value;
              if (isTargetingMode(mode)) dispatch({ t: 'setTargeting', tower: tower.id, mode });
            }}
            title="Which enemy this tower picks"
          >
            {TARGETING_MODES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          )}
          {/* Support towers get the game's only mid-wave action (playtest request). Shown
              only on towers that have one, so the row does not fill with dead buttons. */}
          {tower.support && (
            <button
              type="button"
              className={surging ? 'active' : tower.surgeReady ? 'primary' : ''}
              disabled={!tower.surgeReady}
              title={
                tower.surgeActive
                  ? 'Focused — its aura pays out 4× for the rest of this round'
                  : tower.surgeArmed
                    ? 'Armed — it will be focused for the whole of the next round'
                    : tower.surgeReady
                      ? `Focus this tower: 4× its aura for a whole round, then ${SUPPORT_SURGE_COOLDOWN_WAVES} rounds off. Hotkey ${surgeKey ?? '—'}`
                      : `Recharging — back in ${tower.surgeBackInWaves} round${
                          tower.surgeBackInWaves === 1 ? '' : 's'
                        }`
              }
              onClick={() => dispatch({ t: 'surge', tower: tower.id })}
            >
              {tower.surgeActive
                ? '◈ Focused'
                : tower.surgeArmed
                  ? '◈ Armed'
                  : tower.surgeReady
                    ? `◈ Focus${surgeKey ? ` (${surgeKey})` : ''}`
                    : `◈ ${tower.surgeBackInWaves}`}
            </button>
          )}
          <button
            type="button"
            className={tower.mergeReady ? 'primary' : ''}
            disabled={!tower.mergeReady || phase !== 'build'}
            title={
              // No gold changes hands, and the old wording ("costs you two class slots")
              // read like a price — players went looking for a number that was never
              // there. Say what actually moves, and say it per star: ★3 counts DOUBLE
              // toward synergies, so the second merge gives a slot back where the first
              // spends two.
              tower.mergeReady
                ? tower.star === 2
                  ? 'Free. 3 copies become one ★3 — and a ★3 counts twice toward its classes, so your counts drop by one, not two'
                  : 'Free. 3 copies become one ★2 — your class counts drop by two, since three towers become one'
                : 'Needs 3 copies at the same star'
            }
            onClick={() => dispatch({ t: 'merge', tower: tower.id })}
          >
            ★ Merge
          </button>
          <button
            type="button"
            className={moveMode ? 'active' : ''}
            disabled={phase !== 'build' || placementsLeft <= 0}
            title={
              placementsLeft <= 0
                ? 'No placements left this round — a move costs one'
                : 'Reposition this tower (costs a placement) — click the tile'
            }
            onClick={() => onMoveMode(!moveMode)}
          >
            {moveMode ? 'click a tile…' : 'Move'}
          </button>
          <button
            type="button"
            className="gold"
            disabled={phase !== 'build'}
            title={`Refunds exactly ${tower.sellRefund}g`}
            onClick={() => {
              dispatch({ t: 'sell', tower: tower.id });
              onDeselect();
            }}
          >
            Sell +{tower.sellRefund}g
          </button>
        </div>
      </div>
    </div>
  );
}
